"use client"

import Image from "next/image"
import { Avatar, AvatarFallback } from "@ui/shad"
import { cn } from "@ui/utils"

export interface BulkEmailRecipientData {
  type: "household" | "contact" | "person"
  id: string
  name: string
  email: string | null
  profilePictureUrl?: string | null
}

interface RecipientItemProps {
  recipient: BulkEmailRecipientData
  isSelected?: boolean
  isClickable?: boolean
  onClick?: () => void
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

export function RecipientItem({ recipient, isSelected, isClickable, onClick }: RecipientItemProps) {
  const content = (
    <>
      <Avatar size="sm">
        {recipient.profilePictureUrl ? (
          <Image
            src={recipient.profilePictureUrl}
            alt={recipient.name}
            fill
            sizes="32px"
            className="rounded-full object-cover"
          />
        ) : (
          <AvatarFallback className="text-xs font-medium">{getInitials(recipient.name)}</AvatarFallback>
        )}
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-medium">{recipient.name}</p>
        <p className="text-muted-foreground truncate text-xs">{recipient.email || "No email"}</p>
      </div>
    </>
  )

  if (isClickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-2 px-2 py-1 text-left transition-colors",
          "hover:bg-secondary",
          isSelected && "bg-secondary"
        )}
      >
        {content}
      </button>
    )
  }

  return <div className="flex w-full items-center gap-2 px-2 py-1">{content}</div>
}
